import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { Box, FormControlLabel, Switch, TextField, Typography } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';

import { formatTags } from '../../../utilities/StringUtilities';
import ListSelectorField from '../../form/ListSelectorField';
import FormSection from '../../form/FormSection';
import DataTable from '../../data-table/DataTable';
import { useArrayLoader } from '../../data-table/Loaders';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { ROWSIZE } from '../../data-table/DataTableConstants';
import { filterEntities } from '../../form/ListSelectorUtils';
import { RowFlexLayout } from '../../layout';
import NgpThreeButtonDialog from '../../dialog-three-button/NgpThreeButtonDialog';
import ThresholdSensitivitySlider from './ThresholdSensitivitySlider';
import { useHelpUrl } from '../../../utilities/HelpUtilities';
import { showThresholdError, getThresholdDynamicBounds } from './ThresholdValidation';
import { BAD_INPUT_SENTINEL } from '../../form/NgpNativeNumberField';

const OVERRIDE_THRESHOLD_COLUMN_TITLE = t`AvgResponse Threshold`;
const ADD_DIALOG_MODE_LIST = 'list';
const ADD_DIALOG_MODE_EDIT = 'edit';
const EDIT_SOURCE_ADD_FLOW = 'add-flow';
const EDIT_SOURCE_SELECTED_LIST = 'selected-list';
const OVERRIDE_DIALOG_CONTENT_WIDTH = 800;
const OVERRIDE_DIALOG_EDIT_MIN_HEIGHT = 150;

function asNumber(value) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

const overrideSelectorColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Name`,
        mapping: 'name',
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Tags`,
        mapping: 'tags',
        sortable: false,
        width: 190,
        formatter: (value) => formatTags(value),
    },
];

export default function ThresholdsServiceOverridesSelector({
    tests,
    overrideFieldName,
    onSelectionUpdate,
    setTests,
    setDirtyOverrides,
    setIsSubFormDirty,
    allowAutomaticThresholds,
    hasPrimaryMetricThreshold,
    primaryMetricId,
    primaryMetricUnit,
    getServiceMetricLabel,
    getServiceMetricUnit,
}) {
    const [showAddOverrideDialog, setShowAddOverrideDialog] = useState(false);
    const [addOverrideDialogMode, setAddOverrideDialogMode] = useState(ADD_DIALOG_MODE_LIST);
    const [editingOverrideDraft, setEditingOverrideDraft] = useState(null);
    const [editDialogSource, setEditDialogSource] = useState(EDIT_SOURCE_ADD_FLOW);
    const overrideSelectorRef = useRef({
        selectedIds: [],
        updateSelection: null,
    });

    const autoThresholdsHelpUrl = useHelpUrl('AutomaticThresholds');
    const { getValues } = useFormContext();

    const overrideTests = useMemo(() => tests.filter((test) => test.overrideThreshold), [tests]);
    const overrideTestIds = useMemo(() => overrideTests.map((test) => test._id), [overrideTests]);
    const availableOverrideTests = useMemo(
        () =>
            _.sortBy(
                tests.filter((test) => !test.overrideThreshold),
                (test) => _.toLower(test.name)
            ),
        [tests]
    );

    const handleBeginEditOverride = useCallback(
        (test, source = EDIT_SOURCE_ADD_FLOW) => {
            const formValues = getValues();
            const sourceThresholds =
                _.isArray(test?.thresholds) && test.thresholds.length ? test.thresholds : formValues.thresholds;

            setEditingOverrideDraft({
                _id: test._id,
                name: test.name,
                dynamicThresholds: _.isBoolean(test?.dynamicThresholds)
                    ? !!test.dynamicThresholds
                    : !!formValues.automaticEnabled,
                thresholdsSensitivity: _.isNil(test?.thresholdsSensitivity)
                    ? asNumber(formValues.automaticSensitivity)
                    : asNumber(test.thresholdsSensitivity),
                thresholds: _.cloneDeep(sourceThresholds || []),
            });
            setEditDialogSource(source);
            setAddOverrideDialogMode(ADD_DIALOG_MODE_EDIT);
            setShowAddOverrideDialog(true);
        },
        [getValues]
    );

    const selectedGridColumns = useMemo(() => {
        const primaryMetricThresholdColumn = hasPrimaryMetricThreshold
            ? {
                  type: COLUMN_TYPES.TEXT,
                  title: OVERRIDE_THRESHOLD_COLUMN_TITLE,
                  mapping: 'thresholds',
                  sortable: false,
                  width: 300,
                  formatter: (thresholds, row) => {
                      const rowThresholds = _.isArray(thresholds) ? thresholds : [];
                      const primaryThreshold = _.find(rowThresholds, (threshold) =>
                          _.includes(threshold.metricIds || [], primaryMetricId)
                      );

                      if (!primaryThreshold && !row?.dynamicThresholds) {
                          return t`No Primary metric threshold found`;
                      }

                      if (row?.dynamicThresholds) {
                          return t`AUTOMATIC`;
                      }

                      return t`Degraded: ${primaryThreshold?.degraded}${primaryMetricUnit}, Excessive: ${primaryThreshold?.critical}${primaryMetricUnit}`;
                  },
              }
            : {
                  type: COLUMN_TYPES.TEXT,
                  title: OVERRIDE_THRESHOLD_COLUMN_TITLE,
                  mapping: 'thresholds',
                  sortable: false,
                  width: 300,
                  formatter: () => '',
              };

        return [
            {
                type: COLUMN_TYPES.LINK,
                title: t`Name`,
                mapping: 'name',
                sortable: true,
                width: 300,
                link: {
                    clickHandler: (row) => handleBeginEditOverride(row, EDIT_SOURCE_SELECTED_LIST),
                },
            },
            primaryMetricThresholdColumn,
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Tags`,
                mapping: 'tags',
                sortable: false,
                width: 190,
                formatter: (value) => formatTags(value),
            },
        ];
    }, [hasPrimaryMetricThreshold, primaryMetricId, primaryMetricUnit, handleBeginEditOverride]);

    const availableOverrideFilter = useCallback(
        (entities, filters) => filterEntities(entities, filters, ['name', 'tags']),
        []
    );
    const availableOverrideLoader = useArrayLoader(availableOverrideTests, availableOverrideFilter);

    const addDialogGridColumns = useMemo(
        () => [
            {
                type: COLUMN_TYPES.LINK,
                title: t`Name`,
                mapping: 'name',
                sortable: true,
                width: 320,
                link: {
                    clickHandler: (row) => handleBeginEditOverride(row, EDIT_SOURCE_ADD_FLOW),
                },
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: t`Tags`,
                mapping: 'tags',
                sortable: false,
                width: 190,
                formatter: (value) => formatTags(value),
            },
        ],
        [handleBeginEditOverride]
    );

    const handleOpenAddOverrideDialog = useCallback((context = {}) => {
        overrideSelectorRef.current = {
            selectedIds: context.selectedIds || [],
            updateSelection: context.updateSelection || null,
        };
        setEditingOverrideDraft(null);
        setEditDialogSource(EDIT_SOURCE_ADD_FLOW);
        setAddOverrideDialogMode(ADD_DIALOG_MODE_LIST);
        setShowAddOverrideDialog(true);
    }, []);

    const handleCloseAddOverrideDialog = useCallback(() => {
        setShowAddOverrideDialog(false);
        setEditDialogSource(EDIT_SOURCE_ADD_FLOW);
        setAddOverrideDialogMode(ADD_DIALOG_MODE_LIST);
        setEditingOverrideDraft(null);
    }, []);

    const handleGoBackToOverrideList = useCallback(() => {
        setEditDialogSource(EDIT_SOURCE_ADD_FLOW);
        setAddOverrideDialogMode(ADD_DIALOG_MODE_LIST);
        setEditingOverrideDraft(null);
    }, []);

    const handleOverrideDraftChange = useCallback((field, value) => {
        setEditingOverrideDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    }, []);

    const handleOverrideDraftThresholdChange = useCallback((index, field, value, event) => {
        const storeValue = event?.target?.validity?.badInput ? BAD_INPUT_SENTINEL : value;
        setEditingOverrideDraft((prev) => {
            if (!prev) {
                return prev;
            }

            const nextThresholds = _.cloneDeep(prev.thresholds || []);
            _.set(nextThresholds, `[${index}].${field}`, storeValue);
            return {
                ...prev,
                thresholds: nextThresholds,
            };
        });
    }, []);

    const handleOverrideSensitivityChange = useCallback(
        (event, sliderValue) => {
            const nextValue = _.isArray(sliderValue) ? _.first(sliderValue) : sliderValue;
            handleOverrideDraftChange('thresholdsSensitivity', asNumber(nextValue));
        },
        [handleOverrideDraftChange]
    );

    const overrideThresholdErrors = useMemo(() => {
        if (!editingOverrideDraft || editingOverrideDraft.dynamicThresholds) {
            return [];
        }
        return (editingOverrideDraft.thresholds || []).map((threshold) =>
            showThresholdError(threshold, threshold?.degraded, threshold?.critical)
        );
    }, [editingOverrideDraft]);

    const overrideHasErrors = overrideThresholdErrors.some(Boolean);

    const handleApplyOverrideFromDialog = useCallback(async () => {
        if (!editingOverrideDraft?._id) {
            return;
        }

        const selectedIdsBeforeApply = getValues(overrideFieldName) || [];
        const nextSelectedIds = _.uniq([...selectedIdsBeforeApply, editingOverrideDraft._id]);
        const isNewOverride = !selectedIdsBeforeApply.includes(editingOverrideDraft._id);

        if (isNewOverride && _.isFunction(overrideSelectorRef.current.updateSelection)) {
            overrideSelectorRef.current.updateSelection(nextSelectedIds);
        }

        const normalizedThresholds = _.cloneDeep(editingOverrideDraft.thresholds || []).map((threshold) => ({
            ...threshold,
            degraded: asNumber(threshold.degraded),
            critical: asNumber(threshold.critical),
        }));

        let updatedOverrideTest = null;
        setTests((prev) =>
            prev.map((test) => {
                if (test._id !== editingOverrideDraft._id) {
                    return test;
                }

                updatedOverrideTest = {
                    ...test,
                    overrideThreshold: true,
                    dynamicThresholds: !!editingOverrideDraft.dynamicThresholds,
                    thresholdsSensitivity: asNumber(editingOverrideDraft.thresholdsSensitivity),
                    thresholds: normalizedThresholds,
                };

                return updatedOverrideTest;
            })
        );

        setDirtyOverrides((prev) => ({
            ...prev,
            [editingOverrideDraft._id]: updatedOverrideTest || {
                _id: editingOverrideDraft._id,
                overrideThreshold: true,
                dynamicThresholds: !!editingOverrideDraft.dynamicThresholds,
                thresholdsSensitivity: asNumber(editingOverrideDraft.thresholdsSensitivity),
                thresholds: normalizedThresholds,
            },
        }));

        setIsSubFormDirty(true);
        handleCloseAddOverrideDialog();
    }, [
        editingOverrideDraft,
        handleCloseAddOverrideDialog,
        overrideFieldName,
        getValues,
        setDirtyOverrides,
        setIsSubFormDirty,
        setTests,
    ]);

    return (
        <>
            <FormSection>
                <Typography variant="h6">{t`Test Overrides`}</Typography>
                <Box mt={2}>
                    <ListSelectorField
                        gridColumns={overrideSelectorColumns}
                        selectedGridColumns={selectedGridColumns}
                        dbSelected={overrideTestIds}
                        fieldName={overrideFieldName}
                        dataArray={tests}
                        dataArraySearchFields={['name', 'tags']}
                        addButtonText={t`Add Test Override`}
                        removeButtonText={t`Remove Test Override`}
                        selectedEmptyGridText={t`No overridden tests`}
                        updateSelectionFunction={onSelectionUpdate}
                        setSelectionIsDirty={setIsSubFormDirty}
                        isMultiRemove
                        onAddAction={handleOpenAddOverrideDialog}
                    />
                </Box>
            </FormSection>

            <NgpThreeButtonDialog
                title={
                    addOverrideDialogMode === ADD_DIALOG_MODE_EDIT
                        ? `${editingOverrideDraft?.name || ''} ${t`Threshold Overrides`}`
                        : t`Add Test Override`
                }
                open={showAddOverrideDialog}
                setOpen={setShowAddOverrideDialog}
                onCloseFunc={handleCloseAddOverrideDialog}
                cancelButtonText={t`Cancel`}
                onDisable={handleGoBackToOverrideList}
                callerWillCloseOnDisable
                disableButtonText={t`Go Back`}
                showDisableButton={
                    addOverrideDialogMode === ADD_DIALOG_MODE_EDIT && editDialogSource === EDIT_SOURCE_ADD_FLOW
                }
                onConfirm={handleApplyOverrideFromDialog}
                confirmButtonText={t`Apply`}
                confirmButtonDisabled={overrideHasErrors}
                showConfirmButton={addOverrideDialogMode === ADD_DIALOG_MODE_EDIT}
                callerWillCloseOnConfirm
            >
                <Box style={{ width: OVERRIDE_DIALOG_CONTENT_WIDTH, maxWidth: '90vw' }}>
                    {addOverrideDialogMode === ADD_DIALOG_MODE_LIST ? (
                        <DataTable
                            loader={availableOverrideLoader}
                            columns={addDialogGridColumns}
                            disableCSVExport
                            disableColumnRemover
                            rowSize={ROWSIZE.SMALL}
                            visibleRows={8}
                        />
                    ) : (
                        <Box style={{ minHeight: OVERRIDE_DIALOG_EDIT_MIN_HEIGHT }}>
                            {allowAutomaticThresholds && (
                                <Box mt={1}>
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!editingOverrideDraft?.dynamicThresholds}
                                                    onChange={(event) =>
                                                        handleOverrideDraftChange(
                                                            'dynamicThresholds',
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label={t`Use Automatic Thresholds`}
                                        />
                                        <a
                                            href={autoThresholdsHelpUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={t`View help documentation regarding automatic thresholds`}
                                        >
                                            <HelpIcon fontSize="small" />
                                        </a>
                                    </Box>
                                    {editingOverrideDraft?.dynamicThresholds && (
                                        <Box mt={4} ml={4} maxWidth={460}>
                                            <ThresholdSensitivitySlider
                                                value={_.clamp(
                                                    asNumber(editingOverrideDraft?.thresholdsSensitivity) || 4,
                                                    1,
                                                    5
                                                )}
                                                onChange={handleOverrideSensitivityChange}
                                                sliderWrapStyle={{
                                                    width: '60%',
                                                    paddingLeft: 8,
                                                    paddingRight: 8,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {!editingOverrideDraft?.dynamicThresholds && (
                                <Box mt={2} ml={4}>
                                    {(editingOverrideDraft?.thresholds || []).map((threshold, index) => {
                                        const key = _.join(threshold.metricIds || ['unknown'], '-');
                                        const errorMessage = overrideThresholdErrors[index] || null;
                                        const unit = getServiceMetricUnit
                                            ? getServiceMetricUnit(threshold.metricIds)
                                            : '';
                                        const { degradedMin, degradedMax, excessiveMin, excessiveMax } =
                                            getThresholdDynamicBounds(
                                                threshold,
                                                threshold?.degraded,
                                                threshold?.critical
                                            );
                                        const degradedDisplay =
                                            threshold?.degraded === BAD_INPUT_SENTINEL
                                                ? ''
                                                : _.get(editingOverrideDraft, `thresholds.${index}.degraded`, '');
                                        const criticalDisplay =
                                            threshold?.critical === BAD_INPUT_SENTINEL
                                                ? ''
                                                : _.get(editingOverrideDraft, `thresholds.${index}.critical`, '');

                                        return (
                                            <RowFlexLayout key={`override-threshold-${key}`} vBottom mt={2}>
                                                <Box style={{ maxWidth: 250 }}>
                                                    <Typography variant="body2">
                                                        {getServiceMetricLabel(threshold.metricIds)}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    ml={4}
                                                    display="flex"
                                                    alignItems="flex-end"
                                                    style={{ width: '25%' }}
                                                >
                                                    <TextField
                                                        type="number"
                                                        label={t`Degraded`}
                                                        required
                                                        error={!!errorMessage}
                                                        style={{ flex: 1 }}
                                                        value={degradedDisplay}
                                                        inputProps={{
                                                            min: degradedMin,
                                                            max: degradedMax,
                                                            step: 'any',
                                                        }}
                                                        onChange={(event) =>
                                                            handleOverrideDraftThresholdChange(
                                                                index,
                                                                'degraded',
                                                                event.target.value,
                                                                event
                                                            )
                                                        }
                                                    />
                                                    <Box style={{ minWidth: 32, marginLeft: 4 }}>
                                                        {unit && <Typography variant="body2">{unit}</Typography>}
                                                    </Box>
                                                </Box>
                                                <Box
                                                    ml={4}
                                                    display="flex"
                                                    alignItems="flex-end"
                                                    style={{ width: '25%' }}
                                                >
                                                    <TextField
                                                        type="number"
                                                        label={t`Excessive`}
                                                        required
                                                        error={!!errorMessage}
                                                        style={{ flex: 1 }}
                                                        value={criticalDisplay}
                                                        inputProps={{
                                                            min: excessiveMin,
                                                            max: excessiveMax,
                                                            step: threshold.inputInteger ? 1 : 'any',
                                                        }}
                                                        onChange={(event) =>
                                                            handleOverrideDraftThresholdChange(
                                                                index,
                                                                'critical',
                                                                event.target.value,
                                                                event
                                                            )
                                                        }
                                                    />
                                                    <Box style={{ minWidth: 32, marginLeft: 4 }}>
                                                        {unit && <Typography variant="body2">{unit}</Typography>}
                                                    </Box>
                                                </Box>
                                                <Box ml={4} style={{ width: 180 }}>
                                                    {errorMessage && (
                                                        <Typography
                                                            color="error"
                                                            style={{ fontWeight: 'bold', fontSize: 14 }}
                                                        >
                                                            {errorMessage}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </RowFlexLayout>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </NgpThreeButtonDialog>
        </>
    );
}
